import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getTicketOverlayStyles, getTicketSoldOutVisibility } from 'common/entities/form/styleConfiguration/selectors';
import {
    setTicketOverlayOpacity,
    setTicketOverlayColor,
    setTicketOverlayImageBaseUrl,
    setTicketOverlayImageUrl,
    setTicketSoldOutVisibility,
} from 'common/entities/form/styleConfiguration/actions';
import { TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON } from 'common/entities/ticketType/selectors';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';
import { getColors } from 'common/entities/color/selectors';
import {
    DEFAULT_TICKET_OVERLAY_OPACITY,
    DEFAULT_TICKET_OVERLAY_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { SuiContainer, SuiLabel, SuiCheckbox } from '@splashthat/sui';
import ColorAndImagePicker from 'common/components/ColorAndImagePicker';
import SplashSlider from 'common/components/SplashSlider';
import {
    TICKET_OPACITY_PANEL_LABEL,
    TICKET_OVERLAY_PANEL_LABEL,
    SHOW_SOLD_OUT_LABEL,
} from '../../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../../constants/reusableClassNames';
import { getFormId, getOpenTicketStylingType } from '../../../../selectors';
import { SOLD_OUT_QA } from '../../../../constants/ticketDataQa';

const baseClassName = 'FormStyleTicketEditorSoldOutPanel';

const FormStyleTicketEditorSoldOutPanel = ({
    openTicketType,
    formId,
    color,
    imageUrl,
    imageBaseUrl,
    opacity,
    colorList,
    soldOutVisibility,
    ...props
}) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${SOLD_OUT_QA}--panel`}>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SOLD_OUT_QA}--section-${TICKET_OPACITY_PANEL_LABEL}`}>
            <SuiContainer className={`${baseClassName}__soldOut--checkbox`}>
                <SuiCheckbox
                    label={<SuiLabel text={SHOW_SOLD_OUT_LABEL} />}
                    checked={soldOutVisibility}
                    onChange={() => props.setTicketSoldOutVisibility(formId, !soldOutVisibility, openTicketType)}
                />
            </SuiContainer>
            <SuiLabel text={TICKET_OPACITY_PANEL_LABEL} data-qa={`${SOLD_OUT_QA}--label`} />
            <SplashSlider
                value={opacity * 100}
                onChange={(value) => props.setTicketOverlayOpacity(formId, value / 100, openTicketType)}
                qaId={`${SOLD_OUT_QA}--opacity`}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${SOLD_OUT_QA}--section-${TICKET_OVERLAY_PANEL_LABEL}`}>
            <SuiLabel text={TICKET_OVERLAY_PANEL_LABEL} data-qa={`${SOLD_OUT_QA}--label`} />
            <ColorAndImagePicker
                colorList={colorList}
                color={color}
                changeColor={(id) => props.setTicketOverlayColor(formId, id, openTicketType)}
                qaId={`${SOLD_OUT_QA}--color-image`}
                imageUrl={imageUrl}
                imageBaseUrl={imageBaseUrl}
                onChangeImage={(baseUrl) => props.setTicketOverlayImageBaseUrl(formId, baseUrl, openTicketType)}
                onEditImage={(url) => props.setTicketOverlayImageUrl(formId, url, openTicketType)}
            />
        </SuiContainer>
    </SuiContainer>
);

FormStyleTicketEditorSoldOutPanel.propTypes = {
    openTicketType: PropTypes.oneOf([TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON]).isRequired,
    color: styleShapePropTypes,
    opacity: PropTypes.number,
    imageUrl: PropTypes.string,
    imageBaseUrl: PropTypes.string,
    soldOutVisibility: PropTypes.bool,
    formId: PropTypes.string.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    setTicketOverlayOpacity: PropTypes.func.isRequired,
    setTicketOverlayColor: PropTypes.func.isRequired,
    setTicketOverlayImageBaseUrl: PropTypes.func.isRequired,
    setTicketOverlayImageUrl: PropTypes.func.isRequired,
    setTicketSoldOutVisibility: PropTypes.func.isRequired,
};

FormStyleTicketEditorSoldOutPanel.defaultProps = {
    color: getDefaultStyle(DEFAULT_TICKET_OVERLAY_COLOR),
    imageUrl: '',
    imageBaseUrl: '',
    opacity: DEFAULT_TICKET_OVERLAY_OPACITY,
    soldOutVisibility: true,
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const openTicketType = getOpenTicketStylingType(state);

    const soldOutVisibility = getTicketSoldOutVisibility(state, formId, openTicketType);
    const { color, opacity, imageUrl, imageBaseUrl } = getTicketOverlayStyles(state, formId, openTicketType);

    return {
        formId,
        colorList,
        color,
        opacity,
        imageUrl,
        imageBaseUrl,
        soldOutVisibility,
        openTicketType,
    };
};

const mapDispatchToProps = {
    setTicketOverlayOpacity,
    setTicketOverlayColor,
    setTicketOverlayImageBaseUrl,
    setTicketOverlayImageUrl,
    setTicketSoldOutVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleTicketEditorSoldOutPanel);
